
import Vue from "vue";
import { Component, Prop } from "vue-property-decorator";
import draggable from "vuedraggable";

import { MediaInputValue, MediaV, MediaOptions } from "@/types/media";
import defensiveCopy from "@/util/defensive-copy";
import { pipe } from "fp-ts/lib/function";
import { fold } from "fp-ts/lib/Either";
import * as t from "io-ts";

import MediaUploadDialog from "@/components/Reusable/CustomInputs/MediaInput/MediaUploadDialog.vue";
import MediaPreview from "@/components/Reusable/CustomInputs/MediaInput/MediaPreview.vue";
import Icon from "@/components/UI/Icon.vue";
import NiceButton from "@/components/UI/NiceButton.vue";
import MediaChooserLayer from "@/components/Reusable/CustomInputs/MediaInput/MediaChooserLayer.vue";

@Component({
  components: {
    MediaChooserLayer,
    NiceButton,
    Icon,
    draggable,
    MediaPreview,
    MediaUploadDialog
  }
})
export default class WPMediaInput extends Vue {
  @Prop()
  value?: MediaInputValue;

  @Prop()
  mediaOptions?: MediaOptions;

  drag = false;
  dialogOpen = false;

  dragOptions = {
    animation: 200,
    ghostClass: "is-ghost-image",
    forceFallback: true
  };

  mounted() {
    document.addEventListener("open-media-dialog", this.openDialog);
  }

  beforeDestroy() {
    document.removeEventListener("open-media-dialog", this.openDialog);
  }

  get internalValue() {
    if (this.multiple) {
      // If the input is changed to `multiple` in Element Studio
      // and it has some data in it already,
      // this conditional will make sure we don't lose it.
      const maybeImagesArray = Array.isArray(this.value)
        ? this.value
        : [this.value];

      const type = t.union([t.array(MediaV), t.undefined]);

      return pipe(
        maybeImagesArray,
        type.decode,
        fold(
          () => [],
          arrayOfImages => arrayOfImages
        )
      );
    }

    const maybeImage =
      Array.isArray(this.value) && this.value.length
        ? this.value[0]
        : this.value;

    return pipe(
      maybeImage,
      MediaV.decode,
      fold(
        error => {
          console.debug("Media decoder failed", error);
          return undefined;
        },
        image => image
      )
    );
  }

  get multiple() {
    return !!this.mediaOptions?.multiple;
  }

  get acceptedFileTypes() {
    return this.mediaOptions?.acceptedFileTypes;
  }

  openDialog() {
    this.dialogOpen = true;
  }

  closeDialog() {
    this.dialogOpen = false;
  }

  async onMediaSelected(media: MediaInputValue) {
    const newValue = defensiveCopy(this.value);

    if (Array.isArray(newValue) && Array.isArray(media)) {
      // If we are in gallery mode, we append to the end of the array.
      newValue.push(...media);
      this.updateValue(newValue);
    } else {
      // Otherwise replace the current image with the new one.
      this.updateValue(media);
    }
  }

  removeImageAtIndex(index: number) {
    const newValue = defensiveCopy(this.value);

    if (Array.isArray(newValue)) {
      newValue.splice(index, 1);
      this.$emit("input", newValue);
    }
  }

  updateValue(newValue?: MediaInputValue) {
    this.$emit("input", newValue);
  }

  clear() {
    this.$emit("input", undefined);
  }
}
